import React, { useState, Children, isValidElement, type ReactNode, type FC } from 'react';

interface TabProps {
  id: string;
  label: string;
  icon?: string;
  children: ReactNode;
}

const Tab: FC<TabProps> = ({ id, label, icon, children }) => {
  return null;
};

interface TabsComponentProps {
  children: ReactNode;
}

const TabsComponent:FC<TabsComponentProps> = ({ children }) => {
  const [activeTab, setActiveTab] = useState(null);

  // Extract tab data from children
  const tabs = Children.toArray(children)
    .filter((child): child is React.ReactElement<TabProps> => isValidElement(child) && child.type == Tab)
    .map(child => ({
      id: child.props.id,
      label: child.props.label,
      icon: child.props.icon,
      content: child.props.children,
    }));

  // Set the first tab as active by default if none is active
  React.useEffect(() => {
    if (tabs.length > 0 && !activeTab) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  return (
    <div className="mx-auto w-full space-y-6 bg-white p-8 rounded-lg shadow-lg">
      <div className="border-b border-gray-200 dark:border-gray-700">
        <ul className="flex flex-wrap -mb-px text-sm font-medium text-center text-gray-500 dark:text-gray-400">
          {tabs.map((tab) => (
            <li key={tab.id} className="mr-2">
              <button
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg transition-all duration-300 cursor-pointer ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-blue-600 dark:text-blue-500 dark:border-blue-500'
                    : 'border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'
                }`}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                {tab.icon && <i className={tab.icon + ' fa-solid w-4 h-4 mr-2'} />}
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="dark:bg-gray-800 rounded-lg">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`${
              activeTab === tab.id ? 'block' : 'hidden'
            } text-sm text-gray-500 dark:text-gray-400`}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

// Export both components
export { TabsComponent, Tab };